import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

// UI

import { Box, Button, Flex, Input, Select, Text } from '@chakra-ui/react'
import DatePicker from 'react-datepicker'
import { MdDeleteOutline } from 'react-icons/md'

// Api
import useUser from '@/features/users/useUser'

//Slice
import { setFileHistoryModal } from '@/stores/ui/uiSlice'

//constant
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { ContributorStatus, OfferDocumentTypes } from '@/features/offers/constants'
import { createOptions } from '@/utils/arrayHelper'
import EditableTable from '@/components/Table/EditableTable'
import { createColumnHelper } from '@tanstack/react-table'
import { UncontrolledDatePicker } from '@/components/UncontrolledForm/'
import { InputCell, MultipleFileViewCell } from '@/components/Table/components/Cell'
import { DeleteButton } from '@/components/Elements/Button'
import selectionColumnDef from '@/components/Table/components/Column/selectionColumnDef'
import IndeterminateCheckbox from '@/components/Table/components/IndeterminateCheckbox'
import { useContributorGroup } from '@/features/contributorGroups/useContributorGroup'
import { authSelector } from '@/stores/auth/authSlice'
import { selectOfferDetail } from '@/features/offers/slices/offerDetail.slice'

const ContributorTable = ({
  contributors = [],
  groups = [],
  setSelectedUser,
  removeContributor,
  isOptional = false,
  canRemove = false,
  canEditTable = false,
}) => {
  // Redux
  const { adminMode } = useSelector(selectOfferDetail)
  const { user } = useSelector(authSelector)
  //Local State
  const [rowSelection, setRowSelection] = useState([])

  //Watch Values
  const { useFetchAllContributorGroups } = useContributorGroup()
  const { useFetchAllUsers } = useUser()

  const { data: fetchUsersResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    sortBy: 'fullName',
    orderBy: 1,
  })

  const { getValues, setValue } = useFormContext()
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()

  const offerSubmissionDate = useWatch({ name: `offerSubmissionDate` })
  const createdBy = useWatch({ name: 'createdBy' })
  const creator = useWatch({ name: 'creator' })

  //Translated Values

  const selectedGroups = contributors.map((contributor) => contributor?.group).filter((group) => group !== 'other')
  const allGroupOptions = createOptions(groups, 'name', 'type', false)

  const availableUsers = useMemo(() => {
    if (!fetchUsersResponse?.data) return []

    return fetchUsersResponse?.data.filter((user) => user._id !== creator && user._id !== createdBy)
  }, [createdBy, creator, fetchUsersResponse?.data])

  useEffect(() => {
    setSelectedUser(Object.keys(rowSelection))
  }, [contributors, rowSelection, setSelectedUser])

  const updateContributorTableData = useCallback(
    (rowIndex, columnId, value) => {
      const chosenUser = availableUsers.find((user) => user._id === value)

      const pathName = isOptional ? 'optionalContributors' : 'contributors'

      switch (columnId) {
        case 'group':
          setValue(`${pathName}.${rowIndex}.${columnId}`, value, {
            shouldDirty: true,
          })
          // Also reset the user option
          setValue(`${pathName}.${rowIndex}.user`, { _id: '', email: '' }, { shouldDirty: true })
          break

        case 'user__id': //Tan Stack table with translate user._id to user__id
          if (chosenUser) {
            setValue(`${pathName}.${rowIndex}.user`, chosenUser, {
              shouldDirty: true,
            })
            //Also Syn to field "manager"
            const group = getValues(`${pathName}.${rowIndex}.group`)
            if (group === ContributorGroupType.TECHNICAL_PROJECT_MANAGER) {
              setValue(`manager`, chosenUser._id, { shouldDirty: true })
            }
          } else {
            setValue(`${pathName}.${rowIndex}.user`, { _id: '', email: '' }, { shouldDirty: true })
          }
          break
        case 'dueDate':
          setValue(`${pathName}.${rowIndex}.dueDate`, value ? value : undefined, { shouldDirty: true })

          break
        case 'status':
          setValue(`${pathName}.${rowIndex}.status`, value, {
            shouldDirty: true,
          })
          break
        default:
          throw new Error('Unhandled table column type updated ')
      }
    },
    [availableUsers, getValues, isOptional, setValue]
  )

  const columnDef = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        size: 50,
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <Box px={1}>
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </Box>
        ),
      }),
      columnHelper.accessor('group', {
        header: () => <>Contributor</>,
        size: 230,
        cell: ({ getValue, row, column: { id: columnId }, table }) => {
          const selectedGroups = table.getCoreRowModel().flatRows.map((row) => row.getValue(columnId))
          const currentRowGroup = getValue()
          const isDisabled = table.options.meta?.disabled

          const notSelectedGroupOptions = allGroupOptions
            .filter((option) => !selectedGroups.includes(option?.value) || currentRowGroup === option?.value)
            .concat([{ value: 'other', label: 'Other' }])

          const onChange = ({ target: { value } }) => {
            table.options.meta?.updateData(row.index, columnId, value)
          }

          return (
            <Select value={currentRowGroup} onChange={onChange} disabled={isDisabled || row?.original?.hasEdit}>
              {notSelectedGroupOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )
        },
      }),
      columnHelper.accessor('user._id', {
        header: () => <>Name</>,
        size: 200,

        cell: ({ getValue, row, column: { id: columnId }, isReadOnly, table }) => {
          const group = row.getValue('group')
          const hasEdit = row.original.hasEdit
          const isDisabled = table.options.meta?.disabled

          const selectedUserIds = table.getCoreRowModel().flatRows.map((row) => row.getValue(columnId))

          if (group === 'other') {
          }

          const userOptions = availableUsers
            .filter((user) => {
              if (group === ContributorGroupType.TECHNICAL_PROJECT_MANAGER) {
                return [
                  ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
                  ContributorGroupType.SALES,
                  ContributorGroupType.ENGINEER,
                ].includes(user.contributorGroup)
              }

              if (group === 'other') {
                return ![ContributorGroupType.LEGAL, ContributorGroupType.QS].includes(user.contributorGroup)
              }
              return user.contributorGroup === group
            })
            .map((user) => ({
              value: user._id,
              label: user.fullName,
            }))

          const finalUserOptions =
            userOptions && group === 'other'
              ? userOptions.filter((option) => !selectedUserIds.includes(option.value) || option.value === getValue())
              : userOptions
          const onChange = ({ target: { value } }) => {
            table.options.meta?.updateData(row.index, columnId, value)
          }

          return (
            <Select value={getValue() || ''} onChange={onChange} disabled={isDisabled || row?.original?.hasEdit}>
              <option value=""> </option>
              {finalUserOptions &&
                finalUserOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </Select>
          )
        },
      }),
      columnHelper.accessor('dueDate', {
        size: 170,

        cell: ({ getValue, row, column: { id: columnId }, table }) => {
          const value = getValue()

          return (
            <UncontrolledDatePicker
              value={getValue()}
              maxDate={offerSubmissionDate ? new Date(offerSubmissionDate) : undefined}
              onChange={(date) => {
                table.options.meta?.updateData(row.index, columnId, date)
              }}
              disabled={table.options.meta?.disabled || row?.original?.hasEdit}
            />
          )
        },
      }),
      columnHelper.accessor('status', {
        header: () => <>Approval Status</>,
        size: 120,

        cell: ({ getValue, row: { index, original }, column: { id }, isReadOnly }) => {
          const value = getValue()
          let color = 'black'
          let backgroundColor = 'white'

          switch (value) {
            case ContributorStatus.REQUIRED:
              color = 'black'
              backgroundColor = 'yellow.200'
              break
            case ContributorStatus.REVIEWED:
              backgroundColor = 'green.500'
              color = 'white'
              break
            case ContributorStatus.COMMENTED:
              backgroundColor = 'red.500'
              color = 'white'
              break
          }

          return (
            <Text color={color} bgColor={backgroundColor} py="2" px="1">
              {value}
            </Text>
          )
        },
      }),
      columnHelper.accessor('remark', {
        size: 350,
        cell: (cell) => <InputCell {...cell} extraDisable={!adminMode} />,
      }),
      columnHelper.accessor('attachments', {
        cell: (cell) => <MultipleFileViewCell {...cell} title={'Contributor Attachments'} />,
      }),
      columnHelper.accessor('_id', {
        header: () => <></>,
        size: 100,
        cell: ({ getValue, row: { index, original }, column: { id }, isReadOnly, table }) => {
          if (!canRemove || original?.hasEdit || table.options.meta?.disabled) {
            return <span></span>
          }
          return (
            <DeleteButton
              onClick={() => {
                removeContributor(index)
              }}
            />
          )
        },
      }),
    ],
    [adminMode, allGroupOptions, availableUsers, columnHelper, offerSubmissionDate, removeContributor]
  )

  return (
    <EditableTable
      columnDef={columnDef}
      data={contributors}
      updateData={updateContributorTableData}
      disabled={!canEditTable}
      canSelect={true}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      tableWidth="100%"
    />
  )
}

export default ContributorTable
